import React from "react";
import PropTypes from "prop-types";
import SectionCard from "../../profiles/SectionCard";
import SectionSkeleton from "../../profiles/SectionSkeleton";
import AvatarStack from "../../feeds/AvatarStack";
import { MAX_TEAM_AVATARS } from "../data/use_team_members";

// The design draws these at 40px.
const TEAM_AVATAR_SIZE = 40;

/**
 * TeamMembersCard — chrome half of the "Team Members" rail card on PR and expert profiles (the data half is
 * useTeamMembers). Colleagues at the profile's current employer as an overlapping avatar cluster:
 * no names, no view-all, no edit pen. Each avatar links to that colleague; the "+N" pill does not.
 *
 * Purely presentational, driven entirely by hook state, and self-collapsing: a skeleton while the
 * roster loads, nothing at all once it settles empty. Whether an empty card collapses the whole rail
 * is the page's call — it owns the rail column (see PrProfile).
 *
 * @param {Object} props
 * @param {Array} props.members - AvatarStack items, already capped by the hook
 * @param {number} [props.totalCount] - Distinct colleagues behind `members`; drives the "+N" pill
 * @param {boolean} [props.isLoading] - True while the roster read is in flight
 */
function TeamMembersCard({ members, totalCount = null, isLoading = false }) {
  if (isLoading) return <SectionSkeleton lines={1} />;
  if (!members.length) return null;

  return (
    <SectionCard id="pr-rail-team-members" title="Team Members">
      <AvatarStack avatars={members} size={TEAM_AVATAR_SIZE} max={MAX_TEAM_AVATARS} total={totalCount} />
    </SectionCard>
  );
}

TeamMembersCard.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      avatarUrl: PropTypes.string,
      href: PropTypes.string,
    }),
  ).isRequired,
  totalCount: PropTypes.number,
  isLoading: PropTypes.bool,
};

export default TeamMembersCard;
